import React, { useState, useEffect, useCallback } from 'react'; // Added useCallback
import axios from 'axios';
import { setDocumentTitle } from '../../utils/DocumentTitle';
import moment from 'moment'; // For date formatting
import { toast } from 'react-toastify'; // For notifications

const CompanyList = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null); // Track which company is being deleted
  const [activeTab, setActiveTab] = useState('companies'); // Track active tab
  const [featureFlags, setFeatureFlags] = useState([]); // Track feature flags data
  const [updatingFlag, setUpdatingFlag] = useState(null); // Track which feature flag is being updated

  useEffect(() => {
    setDocumentTitle('Platform Admin');
    fetchCompanies();
  }, []); // Removed fetchCompanies from dependency array as it's defined below

  const fetchCompanies = useCallback(async () => { // Wrap in useCallback
    setLoading(true);
    setError('');
    setDeletingId(null); // Reset deleting state on fetch
    try {
      const token = localStorage.getItem('token'); // Need token for auth
      if (!token) {
        throw new Error('Authentication token not found.');
      }

      const response = await axios.get('/api/platform/companies', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data);
      setFeatureFlags(response.data); // Use same data for feature flags
    } catch (err) {
      console.error('Error fetching companies:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch companies.');
      // Handle specific errors like 403 Forbidden if needed
      if (err.response?.status === 403) {
         setError('Access Denied: You do not have permission to view this page.');
      }
    } finally {
      setLoading(false);
    }
  }, []); // Empty dependency array for useCallback

  const handleDelete = async (companyId, companyName) => {
    // Enhanced confirmation dialog
    const confirmationPrompt = `This action is irreversible and will permanently delete the company "${companyName}" (ID: ${companyId}) along with ALL associated data (users, leads, deals, settings, etc.).\n\nTo confirm, please type exactly: delete ${companyName}`;
    const userInput = window.prompt(confirmationPrompt);
    const expectedInput = `delete ${companyName}`;

    // --- Debugging Delete Confirmation ---
    console.log('--- Delete Confirmation Debug ---');
    console.log('User Input:', userInput);
    console.log('Expected Input:', expectedInput);
    console.log('Types:', typeof userInput, typeof expectedInput);
    console.log('Comparison Result (userInput !== expectedInput):', userInput !== expectedInput);
    // Log character codes for potential hidden characters
    if (userInput) {
      console.log('User Input Char Codes:', userInput.split('').map(c => c.charCodeAt(0)));
    }
    console.log('Expected Input Char Codes:', expectedInput.split('').map(c => c.charCodeAt(0)));
    console.log('---------------------------------');
    // --- End Debugging ---

    if (userInput !== expectedInput) {
      if (userInput !== null) { // Only show alert if they typed something wrong, not if they cancelled
        alert(`Incorrect confirmation phrase entered. Deletion cancelled.`);
      }
      return; // Abort if input doesn't match or if prompt was cancelled
    }

    setDeletingId(companyId); // Set loading state for this specific row
    setError(''); // Clear previous errors
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found.');
      }

      await axios.delete(`/api/platform/companies/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Company "${companyName}" deleted successfully.`);
      // Refresh the list after deletion
      fetchCompanies(); // fetchCompanies is stable due to useCallback

    } catch (err) {
      console.error(`Error deleting company ${companyId}:`, err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to delete company.';
      setError(errorMsg); // Show error specific to deletion
      toast.error(`Failed to delete company: ${errorMsg}`);
      setDeletingId(null); // Reset deleting state on error
    }
    // Note: setLoading(false) is handled by fetchCompanies if successful, or above on error
  };


  const updateFeatureFlag = async (companyId, flagName, enabled) => {
    setUpdatingFlag(`${companyId}-${flagName}`);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/platform/companies/${companyId}/feature-flags`, {
        [flagName]: enabled
      }, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Update local state for both companies and feature flags
      const updateCompany = (company) => 
        company.id === companyId 
          ? { ...company, [flagName]: enabled }
          : company;
          
      setCompanies(prev => prev.map(updateCompany));
      setFeatureFlags(prev => prev.map(updateCompany));
      
      toast.success(`Updated ${flagName} for company ${companyId}`);
    } catch (err) {
      console.error('Error updating feature flag:', err);
      toast.error(`Failed to update feature flag: ${err.response?.data?.message || err.message}`);
    } finally {
      setUpdatingFlag(null);
    }
  };


  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Platform Admin</h1>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('companies')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'companies'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Companies ({companies.length})
          </button>
          <button
            onClick={() => setActiveTab('feature-flags')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'feature-flags'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Feature Flags
          </button>
        </nav>
      </div>

      {loading && <p className="text-center text-gray-600">Loading data...</p>}
      {error && <p className="text-center text-red-600 bg-red-100 p-3 rounded">{error}</p>}

      {!loading && !error && activeTab === 'companies' && (
        <div className="bg-white shadow sm:rounded-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company ID
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User Count
                </th>
                 <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Leads
                </th>
                 <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Deals
                </th>
                 <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   Tasks
                 </th>
                 <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   AI Coach Calls
                 </th>
                 <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   AI Marketing Calls
                 </th>
                 <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   Owner Email
                 </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created At
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Active
                </th>
                 <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
               {companies.length === 0 ? (
                 <tr>
                   <td colSpan="12" className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center"> {/* Increased colSpan again */}
                     No companies found.
                   </td>
                 </tr>
              ) : (
                companies.map((company) => {
                  // Refactor button logic for clarity
                  const isDeleting = deletingId === company.id;
                  const buttonText = isDeleting ? 'Deleting...' : 'Delete';
                  const buttonClassName = `text-red-600 hover:text-red-900 disabled:text-gray-400 disabled:cursor-not-allowed ${isDeleting ? 'opacity-50' : ''}`;

                  return (
                  <tr key={company.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {company.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {company.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {company.user_count}
                    </td>
                     <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {company.lead_count}
                    </td>
                     <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {company.deal_count}
                    </td>
                     <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {company.task_count}
                     </td>
                     <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-center"> {/* Centered the count */}
                      {company.ai_coach_api_calls}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-center"> {/* Centered the count */}
                      {company.ai_marketing_api_calls}
                    </td>
                     <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                       {company.admin_email}
                     </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {moment(company.created_at).format('YYYY-MM-DD HH:mm')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {company.last_active ? moment(company.last_active).format('YYYY-MM-DD HH:mm') : 'Never'}
                    </td>
                     <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleDelete(company.id, company.name)}
                        disabled={isDeleting}
                        className={buttonClassName}
                      >
                        {buttonText}
                      </button>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Feature Flags Tab */}
      {!loading && !error && activeTab === 'feature-flags' && (
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Feature Flags</h3>
            <p className="text-sm text-gray-500 mt-1">Control which companies have access to premium features</p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Users
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SNOV.io Access
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {featureFlags.map((company) => (
                  <tr key={company.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{company.name}</div>
                        <div className="text-sm text-gray-500">ID: {company.id}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {company.user_count} users
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={company.snov_enabled || false}
                            onChange={(e) => updateFeatureFlag(company.id, 'snov_enabled', e.target.checked)}
                            disabled={updatingFlag === `${company.id}-snov_enabled`}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                        {updatingFlag === `${company.id}-snov_enabled` && (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                        )}
                        {company.snov_enabled ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Enabled
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Disabled
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(company.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Info Panel */}
          <div className="bg-blue-50 border-t border-blue-200 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">SNOV.io Feature Flag</h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    SNOV.io access is limited due to API credit constraints. Only enable for companies that need prospect research features.
                    This controls access to:
                  </p>
                  <ul className="mt-2 list-disc list-inside space-y-1">
                    <li>Company prospect search in Prospector module</li>
                    <li>Email discovery for team members</li>
                    <li>SNOV database integration features</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyList;
